import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, Button } from "../components/primitives";
import { Seo } from "../components/Seo";
import { api, IMAGES } from "../lib/api";

const rowInitial = { opacity: 0, y: 24 };
const rowInView = { opacity: 1, y: 0 };
const rowViewport = { once: true, margin: "-60px" };

export default function Services() {
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });

  return (
    <>
      <Seo title="Services" description="Professional mobile salon services — cuts, color, extensions, styling and treatments. Transparent pricing, brought to you." image={IMAGES.salon} />
      <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 items-end" data-testid="services-hero">
        <Reveal>
          <Eyebrow>Professional salon services</Eyebrow>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5rem] leading-[0.98] tracking-tight mt-6 text-ink">
            Craft, brought to you.
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-ink-soft max-w-lg">
            A mobile salon experience without compromise. Transparent pricing, unhurried care, and
            work we're proud to sign our name to.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <img src={IMAGES.salon} alt="Salon" className="rounded-3xl w-full h-[380px] object-cover shadow-xl shadow-black/5" />
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <div className="border-t border-line" data-testid="services-list">
          {services.map((s, i) => (
            <motion.div
              key={s.slug}
              data-testid={`service-row-${s.slug}`}
              initial={rowInitial}
              whileInView={rowInView}
              viewport={rowViewport}
              transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
              className="grid md:grid-cols-12 gap-4 md:gap-8 items-baseline py-8 border-b border-line group"
            >
              <div className="md:col-span-2"><span className="eyebrow">{s.category}</span></div>
              <div className="md:col-span-4">
                <h3 className="font-serif text-3xl text-ink group-hover:text-gold transition-colors">{s.name}</h3>
              </div>
              <div className="md:col-span-4"><p className="text-ink-soft leading-relaxed">{s.description}</p></div>
              <div className="md:col-span-2 md:text-right">
                <p className="font-serif text-2xl text-ink">
                  {s.price_from ? `from $${s.price_from}` : "—"}
                </p>
                <p className="text-sm text-ink-soft mt-1">{s.duration}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-28">
        <Reveal>
          <div className="rounded-3xl bg-surface border border-line p-10 lg:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="font-serif text-3xl sm:text-4xl text-ink">Ready when you are.</h2>
              <p className="mt-3 text-ink-soft text-lg">Online booking is coming soon. For now, send a request and we'll confirm your appointment directly.</p>
            </div>
            <Button as={Link} to="/book" data-testid="services-book-btn">
              Book Appointment <ArrowRight size={16} />
            </Button>
          </div>
        </Reveal>
      </section>
    </>
  );
}
